/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { SelectField, SelectOption } from '@coursera/cds-core';

import { EDUCATION_LEVEL_OPTIONS } from 'bundles/onboarding-common/constants';
import type { EDUCATION_LEVEL_OPTIONS_TYPE } from 'bundles/onboarding-common/constants';
import useLearnerObjectUpdateEducationProfile from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateEducationProfile';
import { getEducationLevelOptionName } from 'bundles/onboarding-common/utils';
import SettingRow from 'bundles/personalization-settings/components/SettingRow';
import { usePersonalizationSettingsContext } from 'bundles/personalization-settings/contexts/PersonalizationSettingsContext';
import { useTrackPersonalizationSelectChange } from 'bundles/personalization-settings/hooks/usePersonalizationEventTracking';
import useSettingSave from 'bundles/personalization-settings/hooks/useSettingSave';

import _t from 'i18n!nls/personalization-settings';

// CDS SelectField's `fullWidth` only widens the outer FormControl, not the
// input box itself (SelectField.tsx never forwards `fullWidth` to the inner
// Select), leaving the visible control shrunk to its content width instead of
// filling the row. Force it via CDS's own public class hook. (SelectField2
// forwards fullWidth correctly, but it's a differently-shaped react-aria API
// with no usage precedent in this codebase - not a drop-in swap for this ticket.)
const styles = {
  select: css`
    min-width: 0;

    .cds-select-field-root {
      width: 100%;
      min-width: 0;
    }
  `,
};

const LABEL_ID = 'education-level-label';

const EDUCATION_LEVEL_VALUES: readonly string[] = Object.values(EDUCATION_LEVEL_OPTIONS);

const isEducationLevel = (value: unknown): value is EDUCATION_LEVEL_OPTIONS_TYPE =>
  typeof value === 'string' && EDUCATION_LEVEL_VALUES.includes(value);

export type EducationLevelSettingItemProps = {
  label: string;
};

const EducationLevelSettingItem = ({ label }: EducationLevelSettingItemProps) => {
  const { values, loading, setFieldValue } = usePersonalizationSettingsContext();
  const { updateEducationProfile } = useLearnerObjectUpdateEducationProfile();
  const optionNames = getEducationLevelOptionName();
  const trackSelectChange = useTrackPersonalizationSelectChange();

  // The mutation doesn't update the read query's cache, so the Select's value
  // is tracked via useSettingSave (seeded from the saved value) rather than
  // bound directly to the query result, which would never change after a
  // selection.
  const {
    value: selectedValue,
    notificationType,
    onChange: handleSelectionChange,
  } = useSettingSave(values.educationLevel, async (value) => {
    if (!value) return { success: true, errors: [] };
    const result = await updateEducationProfile(value);
    if (result.success) {
      setFieldValue('educationLevel', value);
    }
    return result;
  });

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target;
    if (!isEducationLevel(value)) return;
    handleSelectionChange(value);
    trackSelectChange('education_level', [value]);
  };

  return (
    <SettingRow label={label} labelId={LABEL_ID} notificationType={notificationType} loading={loading}>
      <SelectField
        css={styles.select}
        aria-labelledby={LABEL_ID}
        optional
        placeholder={_t('Select an option')}
        value={selectedValue ?? ''}
        onChange={handleChange}
        fullWidth
      >
        {Object.values(EDUCATION_LEVEL_OPTIONS).map((value) => (
          <SelectOption key={value} value={value}>
            {optionNames[value]}
          </SelectOption>
        ))}
      </SelectField>
    </SettingRow>
  );
};

export default EducationLevelSettingItem;
